/**
 * paperworkCategory.js — frontend mirror of the paperwork-category rules.
 *
 * Mirrors backend/app/services/paperwork/category.py (the authority) so the
 * listing form can react instantly while the seller answers the property
 * questions. The server re-derives the category on every save and publish and
 * never trusts a category sent by the client; this module only decides what
 * the form shows and which buttons are enabled.
 *
 * Rule table: docs/architecture/paperwork-categories.md §3.4 (Revision 2).
 * Rows are applied strictly top to bottom, and answers that don't apply to the
 * property type are ignored. Both implementations are pinned to the shared
 * case table __fixtures__/paperworkCategoryCases.json; change the table, both
 * implementations and RULES_VERSION together.
 *
 * Pure: no React, no I/O.
 */

export const RULES_VERSION = 'wi-paperwork-rules.2026-09-29';

// ── Canonical property types ────────────────────────────────────────────
export const PROPERTY_TYPE_SINGLE_FAMILY = 'single_family';
export const PROPERTY_TYPE_CONDO = 'condo';
export const PROPERTY_TYPE_TOWNHOUSE = 'townhouse';
export const PROPERTY_TYPE_MULTI_FAMILY = 'multi_family';
export const PROPERTY_TYPE_MOBILE_HOME = 'mobile_home';
export const PROPERTY_TYPE_LAND = 'land';
export const PROPERTY_TYPE_COOPERATIVE = 'cooperative'; // always blocked
export const PROPERTY_TYPE_TIMESHARE = 'timeshare'; // always blocked
export const PROPERTY_TYPE_MIXED_USE = 'mixed_use'; // always blocked

// ── Categories, statuses, disclosure basis ──────────────────────────────
export const CATEGORY_RESIDENTIAL = 'residential';
export const CATEGORY_CONDO = 'condo';
export const CATEGORY_VACANT_LAND = 'vacant_land';
export const PAPERWORK_CATEGORIES = [CATEGORY_RESIDENTIAL, CATEGORY_CONDO, CATEGORY_VACANT_LAND];

export const STATUS_OK = 'ok';
export const STATUS_NEEDS_INFO = 'needs_info';
export const STATUS_UNKNOWN = 'unknown';
export const STATUS_BLOCKED = 'blocked';

export const DISCLOSURE_BASIS_STATUTORY = 'statutory';
export const DISCLOSURE_BASIS_VOLUNTARY = 'voluntary';

// ── Qualifiers (seller-attested facts stored in mls_profile) ─────────────
export const QUALIFIER_CONDOMINIUM_UNIT = 'condominium_unit';
export const QUALIFIER_MOBILE_HOME_LAND = 'mobile_home_land';
export const QUALIFIER_MOBILE_HOME_AFFIXED = 'mobile_home_affixed';
export const QUALIFIER_MOBILE_HOME_TITLE = 'mobile_home_title';
export const QUALIFIER_UNIT_COUNT = 'unit_count';
export const QUALIFIER_HAS_COMMERCIAL_SPACE = 'has_commercial_space';
export const QUALIFIER_LAND_BUILDINGS = 'land_buildings';
export const QUALIFIER_OPERATING_FARM = 'operating_farm';
/** Asked on the condo-documents package, not the listing; never routes. */
export const QUALIFIER_SMALL_CONDOMINIUM = 'small_condominium';

/** The qualifiers the listing form asks (small_condominium is not one). */
export const LISTING_QUALIFIER_KEYS = [
  QUALIFIER_CONDOMINIUM_UNIT,
  QUALIFIER_MOBILE_HOME_LAND,
  QUALIFIER_MOBILE_HOME_AFFIXED,
  QUALIFIER_MOBILE_HOME_TITLE,
  QUALIFIER_UNIT_COUNT,
  QUALIFIER_HAS_COMMERCIAL_SPACE,
  QUALIFIER_LAND_BUILDINGS,
  QUALIFIER_OPERATING_FARM,
];

export const ANSWER_YES = 'yes';
export const ANSWER_NO = 'no';
export const ANSWER_NOT_SURE = 'not_sure';
export const CONDOMINIUM_UNIT_COOPERATIVE = 'cooperative';
export const MOBILE_HOME_LAND_OWNED = 'owned';
export const MOBILE_HOME_LAND_LEASED = 'leased';
export const MOBILE_HOME_TITLE_NONE = 'none';
export const MOBILE_HOME_TITLE_OUTSTANDING = 'outstanding';
export const LAND_BUILDINGS_NONE = 'none';
export const LAND_BUILDINGS_OUTBUILDINGS_ONLY = 'outbuildings_only';
export const LAND_BUILDINGS_DWELLING = 'dwelling';

/** Wis. Stat. § 709.001(5)(a): 1 to 4 dwelling units. */
export const MAX_RESIDENTIAL_UNITS = 4;

export const MISSING_PROPERTY_TYPE = 'property_type';

// ── Block codes (stable machine codes shared with the server) ───────────
export const BLOCK_COOPERATIVE = 'cooperative';
export const BLOCK_TIMESHARE = 'timeshare';
export const BLOCK_MIXED_USE = 'mixed_use';
export const BLOCK_MULTI_FAMILY_5_PLUS = 'multi_family_5_plus';
export const BLOCK_MOBILE_HOME_LEASED_LAND = 'mobile_home_leased_land';
export const BLOCK_MOBILE_HOME_NOT_AFFIXED = 'mobile_home_not_affixed';
export const BLOCK_MOBILE_HOME_TITLED = 'mobile_home_titled';
export const BLOCK_FARM_WITH_BUILDINGS = 'farm_with_buildings';
export const BLOCK_VACANT_LAND_CONDO = 'vacant_land_condo';
export const BLOCK_LAND_HAS_DWELLING = 'land_has_dwelling';

/** Legal §2.2: every block refers to an attorney except this correctable mis-type. */
const BLOCKS_WITHOUT_ATTORNEY_REFERRAL = new Set([BLOCK_LAND_HAS_DWELLING]);

/** Copy key for a condo answered "not a condominium unit" (re-type the listing). */
export const CONDO_ANSWERED_NO_MESSAGE_KEY = 'paperwork.needs_info.condo_answered_no';

// ── Normalization tables (keys compared after normalizeKey) ─────────────
const PROPERTY_TYPE_ALIASES = {
  // single family
  'single family': PROPERTY_TYPE_SINGLE_FAMILY,
  'single family residential': PROPERTY_TYPE_SINGLE_FAMILY,
  'single family home': PROPERTY_TYPE_SINGLE_FAMILY,
  singlefamily: PROPERTY_TYPE_SINGLE_FAMILY,
  sfr: PROPERTY_TYPE_SINGLE_FAMILY,
  detached: PROPERTY_TYPE_SINGLE_FAMILY,
  residential: PROPERTY_TYPE_SINGLE_FAMILY,
  // condo
  condo: PROPERTY_TYPE_CONDO,
  condominium: PROPERTY_TYPE_CONDO,
  // townhouse
  townhouse: PROPERTY_TYPE_TOWNHOUSE,
  townhome: PROPERTY_TYPE_TOWNHOUSE,
  'town house': PROPERTY_TYPE_TOWNHOUSE,
  'town home': PROPERTY_TYPE_TOWNHOUSE,
  // multi-family
  'multi family': PROPERTY_TYPE_MULTI_FAMILY,
  multifamily: PROPERTY_TYPE_MULTI_FAMILY,
  duplex: PROPERTY_TYPE_MULTI_FAMILY,
  triplex: PROPERTY_TYPE_MULTI_FAMILY,
  fourplex: PROPERTY_TYPE_MULTI_FAMILY,
  apartment: PROPERTY_TYPE_MULTI_FAMILY,
  // mobile / manufactured ("mobile" is the legacy backfill value)
  'mobile home': PROPERTY_TYPE_MOBILE_HOME,
  mobile: PROPERTY_TYPE_MOBILE_HOME,
  manufactured: PROPERTY_TYPE_MOBILE_HOME,
  'manufactured home': PROPERTY_TYPE_MOBILE_HOME,
  // land
  land: PROPERTY_TYPE_LAND,
  lot: PROPERTY_TYPE_LAND,
  'lot/land': PROPERTY_TYPE_LAND,
  'land/lot': PROPERTY_TYPE_LAND,
  'vacant land': PROPERTY_TYPE_LAND,
  'vacant lot': PROPERTY_TYPE_LAND,
  // always blocked (a co-op conveys shares and a lease, never a deed)
  cooperative: PROPERTY_TYPE_COOPERATIVE,
  'co op': PROPERTY_TYPE_COOPERATIVE,
  coop: PROPERTY_TYPE_COOPERATIVE,
  'housing cooperative': PROPERTY_TYPE_COOPERATIVE,
  'condo/co op': PROPERTY_TYPE_COOPERATIVE,
  timeshare: PROPERTY_TYPE_TIMESHARE,
  'time share': PROPERTY_TYPE_TIMESHARE,
  'mixed use': PROPERTY_TYPE_MIXED_USE,
  mixeduse: PROPERTY_TYPE_MIXED_USE,
  'mixed use (homes + commercial space)': PROPERTY_TYPE_MIXED_USE,
};

const NOT_SURE_ALIASES = {
  'not sure': ANSWER_NOT_SURE,
  unsure: ANSWER_NOT_SURE,
  unknown: ANSWER_NOT_SURE,
  "i'm not sure": ANSWER_NOT_SURE,
  'im not sure': ANSWER_NOT_SURE,
  "don't know": ANSWER_NOT_SURE,
  'dont know': ANSWER_NOT_SURE,
};

const YES_NO_ALIASES = {
  yes: ANSWER_YES,
  y: ANSWER_YES,
  true: ANSWER_YES,
  no: ANSWER_NO,
  n: ANSWER_NO,
  false: ANSWER_NO,
};

const YES_NO_NOT_SURE_ALIASES = { ...YES_NO_ALIASES, ...NOT_SURE_ALIASES };

const CONDOMINIUM_UNIT_ALIASES = {
  ...YES_NO_NOT_SURE_ALIASES,
  'yes, it is a condominium unit': ANSWER_YES,
  cooperative: CONDOMINIUM_UNIT_COOPERATIVE,
  'co op': CONDOMINIUM_UNIT_COOPERATIVE,
  coop: CONDOMINIUM_UNIT_COOPERATIVE,
  'housing cooperative': CONDOMINIUM_UNIT_COOPERATIVE,
  "no, it's a housing cooperative (i own shares)": CONDOMINIUM_UNIT_COOPERATIVE,
  "no, it's a housing cooperative (i own shares, not a deed)": CONDOMINIUM_UNIT_COOPERATIVE,
};

// owned | leased only: the form offers no "not sure" here (legal §1.2).
const MOBILE_HOME_LAND_ALIASES = {
  owned: MOBILE_HOME_LAND_OWNED,
  own: MOBILE_HOME_LAND_OWNED,
  'i own the land': MOBILE_HOME_LAND_OWNED,
  'yes, i own the land and am selling it with the home': MOBILE_HOME_LAND_OWNED,
  leased: MOBILE_HOME_LAND_LEASED,
  lease: MOBILE_HOME_LAND_LEASED,
  rented: MOBILE_HOME_LAND_LEASED,
  rent: MOBILE_HOME_LAND_LEASED,
  'leased lot': MOBILE_HOME_LAND_LEASED,
  'the lot is leased or rented': MOBILE_HOME_LAND_LEASED,
  'no, the land is rented or leased': MOBILE_HOME_LAND_LEASED,
};

const MOBILE_HOME_TITLE_ALIASES = {
  none: MOBILE_HOME_TITLE_NONE,
  no: MOBILE_HOME_TITLE_NONE,
  surrendered: MOBILE_HOME_TITLE_NONE,
  'no title': MOBILE_HOME_TITLE_NONE,
  outstanding: MOBILE_HOME_TITLE_OUTSTANDING,
  yes: MOBILE_HOME_TITLE_OUTSTANDING,
  titled: MOBILE_HOME_TITLE_OUTSTANDING,
  ...NOT_SURE_ALIASES,
};

const LAND_BUILDINGS_ALIASES = {
  none: LAND_BUILDINGS_NONE,
  'no buildings': LAND_BUILDINGS_NONE,
  'no buildings or structures': LAND_BUILDINGS_NONE,
  'outbuildings only': LAND_BUILDINGS_OUTBUILDINGS_ONLY,
  outbuildings: LAND_BUILDINGS_OUTBUILDINGS_ONLY,
  dwelling: LAND_BUILDINGS_DWELLING,
  ...NOT_SURE_ALIASES,
};

// "No, or I'm not sure" is one answer (legal §5.2): not sure means no.
const SMALL_CONDOMINIUM_ALIASES = {
  ...YES_NO_ALIASES,
  ...Object.fromEntries(Object.keys(NOT_SURE_ALIASES).map((alias) => [alias, ANSWER_NO])),
  'yes, the declaration adopts the small condominium rules': ANSWER_YES,
  "no, or i'm not sure": ANSWER_NO,
};

// ── Normalizers ─────────────────────────────────────────────────────────

const has = (table, key) => Object.prototype.hasOwnProperty.call(table, key);

/**
 * The one whitespace set both implementations trim and collapse (anvil F8):
 * exactly ECMAScript's WhiteSpace + LineTerminator, including the U+FEFF byte
 * order mark and excluding U+0085 NEL and U+001C-U+001F. Spelled out so it
 * matches `WHITESPACE_CHARS` in backend/app/services/paperwork/category.py
 * character for character, rather than relying on `trim()` / `\s`.
 */
export const WHITESPACE_CHARS =
  '\t\n\u000b\u000c\r \u00a0\u1680\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007' +
  '\u2008\u2009\u200a\u2028\u2029\u202f\u205f\u3000\ufeff';
const WHITESPACE_CLASS = `[${WHITESPACE_CHARS}]`;
const EDGE_WHITESPACE = new RegExp(`^${WHITESPACE_CLASS}+|${WHITESPACE_CLASS}+$`, 'g');
const RUN_OF_WHITESPACE = new RegExp(`${WHITESPACE_CLASS}+`, 'g');

/** Strip `WHITESPACE_CHARS` from both ends. */
const trimWhitespace = (raw) => raw.replace(EDGE_WHITESPACE, '');

/** Lower-case, map '-'/'_' to spaces and collapse whitespace (`WHITESPACE_CHARS`). */
export function normalizeKey(raw) {
  const text = trimWhitespace(raw).toLowerCase().replace(/[-_]/g, ' ');
  return trimWhitespace(text.replace(RUN_OF_WHITESPACE, ' '));
}

/** True for null/undefined and empty or whitespace-only strings (unanswered). */
const isBlank = (raw) =>
  raw === null || raw === undefined || (typeof raw === 'string' && !trimWhitespace(raw));

/**
 * Canonical property type for a label, alias or canonical value.
 *
 * @param {*} raw - usually `mls_profile.property_type`
 * @returns {string|null} a canonical type, or null when blank/unrecognized
 */
export function normalizePropertyType(raw) {
  if (isBlank(raw) || typeof raw !== 'string') return null;
  const key = normalizeKey(raw);
  return has(PROPERTY_TYPE_ALIASES, key) ? PROPERTY_TYPE_ALIASES[key] : null;
}

function normalizeChoice(raw, aliases, { allowBool = false } = {}) {
  if (typeof raw === 'boolean') {
    if (!allowBool) return null;
    const key = raw ? 'true' : 'false';
    return has(aliases, key) ? aliases[key] : null;
  }
  if (typeof raw !== 'string' || isBlank(raw)) return null;
  const key = normalizeKey(raw);
  return has(aliases, key) ? aliases[key] : null;
}

// ASCII digits only (no `u` flag, so `\d` is [0-9]): "1_0", full-width "４"
// and Arabic-Indic "٤" are invalid, never parsed loosely (anvil F8). Mirrors
// `_NUMERIC_TEXT_RE` in category.py.
const NUMERIC_TEXT = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;

/** A whole number >= 1, or null ("3.0" and " 3 " count; "2.5" doesn't). */
export function normalizeUnitCount(raw) {
  if (typeof raw === 'boolean' || isBlank(raw)) return null;
  let value = null;
  if (typeof raw === 'number') {
    value = Number.isInteger(raw) ? raw : null;
  } else if (typeof raw === 'string') {
    const text = trimWhitespace(raw);
    if (!NUMERIC_TEXT.test(text)) return null;
    const parsed = Number(text);
    value = Number.isInteger(parsed) ? parsed : null;
  }
  if (value === null || value < 1) return null;
  return value;
}

const QUALIFIER_PARSERS = [
  [QUALIFIER_CONDOMINIUM_UNIT, (v) => normalizeChoice(v, CONDOMINIUM_UNIT_ALIASES, { allowBool: true })],
  [QUALIFIER_MOBILE_HOME_LAND, (v) => normalizeChoice(v, MOBILE_HOME_LAND_ALIASES)],
  [QUALIFIER_MOBILE_HOME_AFFIXED, (v) => normalizeChoice(v, YES_NO_NOT_SURE_ALIASES, { allowBool: true })],
  [QUALIFIER_MOBILE_HOME_TITLE, (v) => normalizeChoice(v, MOBILE_HOME_TITLE_ALIASES)],
  [QUALIFIER_UNIT_COUNT, normalizeUnitCount],
  [QUALIFIER_HAS_COMMERCIAL_SPACE, (v) => normalizeChoice(v, YES_NO_ALIASES, { allowBool: true })],
  [QUALIFIER_LAND_BUILDINGS, (v) => normalizeChoice(v, LAND_BUILDINGS_ALIASES)],
  [QUALIFIER_OPERATING_FARM, (v) => normalizeChoice(v, YES_NO_ALIASES, { allowBool: true })],
  [QUALIFIER_SMALL_CONDOMINIUM, (v) => normalizeChoice(v, SMALL_CONDOMINIUM_ALIASES, { allowBool: true })],
];

/**
 * The valid qualifier answers in a profile, canonicalized; invalid or blank
 * answers are omitted (treated as unanswered).
 *
 * @param {object|null} profile - an `mls_profile`
 * @returns {object}
 */
export function normalizeQualifiers(profile) {
  if (!profile || typeof profile !== 'object' || Array.isArray(profile)) return {};
  const result = {};
  QUALIFIER_PARSERS.forEach(([key, parse]) => {
    const value = parse(profile[key]);
    if (value !== null) result[key] = value;
  });
  return result;
}

// ── Derivation (design §3.4, Revision 2) ────────────────────────────────

const makeResult = (fields) => ({
  propertyType: null,
  category: null,
  status: STATUS_NEEDS_INFO,
  missing: [],
  blockCode: null,
  blockField: null,
  disclosureBasis: null,
  attorneyReferral: false,
  messageKey: null,
  rulesVersion: RULES_VERSION,
  ...fields,
});

const ok = (propertyType, category, basis = DISCLOSURE_BASIS_STATUTORY) =>
  makeResult({ propertyType, category, status: STATUS_OK, disclosureBasis: basis });

const block = (propertyType, code, field) =>
  makeResult({
    propertyType,
    status: STATUS_BLOCKED,
    blockCode: code,
    blockField: `mls_profile.${field}`,
    attorneyReferral: !BLOCKS_WITHOUT_ATTORNEY_REFERRAL.has(code),
    messageKey: `paperwork.block.${code}`,
  });

/** needs_info (or unknown, for a missing type); `not_sure` adds the referral. */
const needs = (propertyType, field, answer = null, messageKey = null) => {
  const notSure = answer === ANSWER_NOT_SURE;
  return makeResult({
    propertyType,
    status: field === MISSING_PROPERTY_TYPE ? STATUS_UNKNOWN : STATUS_NEEDS_INFO,
    missing: [field],
    attorneyReferral: notSure,
    messageKey:
      messageKey ||
      (notSure ? `paperwork.needs_info.not_sure.${field}` : `paperwork.missing.${field}`),
  });
};

function deriveHouseWithCondoQuestion(ptype, a) {
  const unit = a[QUALIFIER_CONDOMINIUM_UNIT];
  if (unit === ANSWER_YES) return ok(ptype, CATEGORY_CONDO);
  if (unit === ANSWER_NO) return ok(ptype, CATEGORY_RESIDENTIAL);
  if (unit === CONDOMINIUM_UNIT_COOPERATIVE) return block(ptype, BLOCK_COOPERATIVE, QUALIFIER_CONDOMINIUM_UNIT);
  return needs(ptype, QUALIFIER_CONDOMINIUM_UNIT, unit);
}

function deriveCondo(ptype, a) {
  const unit = a[QUALIFIER_CONDOMINIUM_UNIT];
  if (unit === ANSWER_YES) return ok(ptype, CATEGORY_CONDO);
  if (unit === CONDOMINIUM_UNIT_COOPERATIVE) return block(ptype, BLOCK_COOPERATIVE, QUALIFIER_CONDOMINIUM_UNIT);
  if (unit === ANSWER_NO) {
    return needs(ptype, QUALIFIER_CONDOMINIUM_UNIT, unit, CONDO_ANSWERED_NO_MESSAGE_KEY);
  }
  return needs(ptype, QUALIFIER_CONDOMINIUM_UNIT, unit);
}

function deriveMultiFamily(ptype, a) {
  const commercial = a[QUALIFIER_HAS_COMMERCIAL_SPACE];
  if (commercial === ANSWER_YES) return block(ptype, BLOCK_MIXED_USE, QUALIFIER_HAS_COMMERCIAL_SPACE);
  if (commercial === undefined) return needs(ptype, QUALIFIER_HAS_COMMERCIAL_SPACE);
  const units = a[QUALIFIER_UNIT_COUNT];
  if (units === undefined) return needs(ptype, QUALIFIER_UNIT_COUNT);
  if (units > MAX_RESIDENTIAL_UNITS) return block(ptype, BLOCK_MULTI_FAMILY_5_PLUS, QUALIFIER_UNIT_COUNT);
  return ok(ptype, CATEGORY_RESIDENTIAL);
}

function deriveMobileHome(ptype, a) {
  const land = a[QUALIFIER_MOBILE_HOME_LAND];
  if (land === MOBILE_HOME_LAND_LEASED) {
    return block(ptype, BLOCK_MOBILE_HOME_LEASED_LAND, QUALIFIER_MOBILE_HOME_LAND);
  }
  if (land !== MOBILE_HOME_LAND_OWNED) return needs(ptype, QUALIFIER_MOBILE_HOME_LAND, land);
  const affixed = a[QUALIFIER_MOBILE_HOME_AFFIXED];
  if (affixed === ANSWER_NO) {
    return block(ptype, BLOCK_MOBILE_HOME_NOT_AFFIXED, QUALIFIER_MOBILE_HOME_AFFIXED);
  }
  if (affixed !== ANSWER_YES) return needs(ptype, QUALIFIER_MOBILE_HOME_AFFIXED, affixed);
  const title = a[QUALIFIER_MOBILE_HOME_TITLE];
  if (title === MOBILE_HOME_TITLE_OUTSTANDING) {
    return block(ptype, BLOCK_MOBILE_HOME_TITLED, QUALIFIER_MOBILE_HOME_TITLE);
  }
  if (title !== MOBILE_HOME_TITLE_NONE) return needs(ptype, QUALIFIER_MOBILE_HOME_TITLE, title);
  return ok(ptype, CATEGORY_RESIDENTIAL);
}

function deriveLand(ptype, a) {
  const unit = a[QUALIFIER_CONDOMINIUM_UNIT];
  if (unit === ANSWER_YES) return block(ptype, BLOCK_VACANT_LAND_CONDO, QUALIFIER_CONDOMINIUM_UNIT);
  if (unit === CONDOMINIUM_UNIT_COOPERATIVE) return block(ptype, BLOCK_COOPERATIVE, QUALIFIER_CONDOMINIUM_UNIT);
  if (unit !== ANSWER_NO) return needs(ptype, QUALIFIER_CONDOMINIUM_UNIT, unit);
  const buildings = a[QUALIFIER_LAND_BUILDINGS];
  if (buildings === undefined || buildings === ANSWER_NOT_SURE) {
    return needs(ptype, QUALIFIER_LAND_BUILDINGS, buildings);
  }
  if (buildings === LAND_BUILDINGS_DWELLING) {
    return block(ptype, BLOCK_LAND_HAS_DWELLING, QUALIFIER_LAND_BUILDINGS);
  }
  if (buildings === LAND_BUILDINGS_NONE) {
    return ok(ptype, CATEGORY_VACANT_LAND, DISCLOSURE_BASIS_STATUTORY);
  }
  const farm = a[QUALIFIER_OPERATING_FARM]; // buildings === outbuildings_only
  if (farm === undefined) return needs(ptype, QUALIFIER_OPERATING_FARM);
  if (farm === ANSWER_YES) return block(ptype, BLOCK_FARM_WITH_BUILDINGS, QUALIFIER_OPERATING_FARM);
  // User override (decision b): the VLDR is collected, labeled voluntary.
  return ok(ptype, CATEGORY_VACANT_LAND, DISCLOSURE_BASIS_VOLUNTARY);
}

const RULES = {
  [PROPERTY_TYPE_COOPERATIVE]: (p) => block(p, BLOCK_COOPERATIVE, MISSING_PROPERTY_TYPE),
  [PROPERTY_TYPE_TIMESHARE]: (p) => block(p, BLOCK_TIMESHARE, MISSING_PROPERTY_TYPE),
  [PROPERTY_TYPE_MIXED_USE]: (p) => block(p, BLOCK_MIXED_USE, MISSING_PROPERTY_TYPE),
  [PROPERTY_TYPE_SINGLE_FAMILY]: deriveHouseWithCondoQuestion,
  [PROPERTY_TYPE_TOWNHOUSE]: deriveHouseWithCondoQuestion,
  [PROPERTY_TYPE_CONDO]: deriveCondo,
  [PROPERTY_TYPE_MULTI_FAMILY]: deriveMultiFamily,
  [PROPERTY_TYPE_MOBILE_HOME]: deriveMobileHome,
  [PROPERTY_TYPE_LAND]: deriveLand,
};

/**
 * Derive the paperwork category from a property type and qualifier answers.
 *
 * @param {string|null} propertyType - canonical type or a raw label
 * @param {object|null} qualifiers - a profile or normalized qualifiers
 * @returns {{propertyType, category, status, missing, blockCode, blockField,
 *   disclosureBasis, attorneyReferral, messageKey, rulesVersion}}
 */
export function derivePaperworkCategory(propertyType, qualifiers) {
  const ptype = has(RULES, propertyType || '') ? propertyType : normalizePropertyType(propertyType);
  if (!ptype) return needs(null, MISSING_PROPERTY_TYPE);
  return RULES[ptype](ptype, normalizeQualifiers(qualifiers));
}

/** Derive straight from an `mls_profile` (null is treated as empty). */
export function deriveFromProfile(profile) {
  const safe = profile && typeof profile === 'object' && !Array.isArray(profile) ? profile : {};
  return derivePaperworkCategory(normalizePropertyType(safe.property_type), normalizeQualifiers(safe));
}

/**
 * Which paperwork a result selects, as one comparable string, e.g.
 * "vacant_land:voluntary"; null unless the result is ok. A change in this key
 * is a change in category or disclosure basis, which voids a completed report.
 */
export function paperworkKey(result) {
  if (!result || result.status !== STATUS_OK || !result.category) return null;
  return `${result.category}:${result.disclosureBasis || DISCLOSURE_BASIS_STATUTORY}`;
}

/** True when the result blocks saving (hard blocks are never persisted). */
export const isBlocked = (result) => result?.status === STATUS_BLOCKED;
